// src/pages/Admin/Dashboard/Events/hooks/useEvents.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addEvent,
  deleteEvent,
  getAllEvents,
  toggleEventActive,
  toggleEventRegister,
  updateEvent as updateEventApi,
  uploadEventImage,
} from "../../../../../services/eventsServices";

import {
  buildEventsStats,
  filterAndSortEvents,
  isDemoUser,
  normalizeEventPayload,
  safeJsonParse,
} from "../utils/eventsUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  registration: "all",
  sortOrder: "start_asc",
};

export default function useEvents() {
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshEvents = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);
      toast.error("Erreur lors de la récupération des événements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshEvents();
  }, [loadUserMode, refreshEvents]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const uploadEventImageFile = useCallback(async (imageFile) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);

    const uploadResponse = await uploadEventImage(formData);

    return (
      uploadResponse?.filePath ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      null
    );
  }, []);

  const buildPayloadWithImage = useCallback(
    async (formData) => {
      const payload = normalizeEventPayload(formData);

      if (formData.imageFile) {
        const uploadedImageUrl = await uploadEventImageFile(formData.imageFile);

        if (uploadedImageUrl) {
          payload.photo_url = uploadedImageUrl;
        }
      }

      delete payload.imageFile;

      return payload;
    },
    [uploadEventImageFile]
  );

  const filteredEvents = useMemo(
    () => filterAndSortEvents(events, filters),
    [events, filters]
  );

  const stats = useMemo(
    () => buildEventsStats(events, filteredEvents),
    [events, filteredEvents]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedEvent(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (event) => {
      if (guardDemoMode()) return;

      setSelectedEvent(event);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((event) => {
    setSelectedEvent(event);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedEvent(null);
  }, []);

  const createEvent = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await addEvent(payload);

        toast.success("Événement créé avec succès.");
        closeDrawer();
        await refreshEvents();
      } catch (error) {
        console.error("Erreur lors de la création de l'événement :", error);
        toast.error("Erreur lors de la création de l'événement.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshEvents]
  );

  const updateEvent = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await updateEventApi(payload);

        toast.success("Événement modifié avec succès.");
        closeDrawer();
        await refreshEvents();
      } catch (error) {
        console.error("Erreur lors de la modification de l'événement :", error);
        toast.error("Erreur lors de la modification de l'événement.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshEvents]
  );

  const askDelete = useCallback(
    (eventId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(eventId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (eventId) => {
      if (guardDemoMode()) return;

      try {
        await deleteEvent(eventId);

        toast.success("Événement supprimé avec succès.");
        setDeleteCandidateId(null);
        await refreshEvents();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'événement :", error);
        toast.error("Erreur lors de la suppression de l'événement.");
      }
    },
    [guardDemoMode, refreshEvents]
  );

  const toggleActive = useCallback(
    async (eventId) => {
      if (guardDemoMode()) return;

      try {
        await toggleEventActive(eventId);
        await refreshEvents();
      } catch (error) {
        console.error("Erreur lors du changement de statut :", error);
        toast.error("Erreur lors du changement de statut.");
      }
    },
    [guardDemoMode, refreshEvents]
  );

  const toggleRegister = useCallback(
    async (eventId) => {
      if (guardDemoMode()) return;

      try {
        await toggleEventRegister(eventId);
        await refreshEvents();
      } catch (error) {
        console.error("Erreur lors du changement des inscriptions :", error);
        toast.error("Erreur lors du changement des inscriptions.");
      }
    },
    [guardDemoMode, refreshEvents]
  );

  return {
    loading,
    saving,
    filters,
    setFilter,
    events,
    filteredEvents,
    selectedEvent,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshEvents,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createEvent,
      updateEvent,
      askDelete,
      cancelDelete,
      confirmDelete,
      toggleActive,
      toggleRegister,
    },
  };
}